import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Icon, { GLYPHS } from '../../../../common/icon';
import ContextMenu from './ContextMenu';
import NotesModal from './NotesModal';
import { t } from '../../../../../modules/infra/i18n';

export const WalkInSlotStatus = {
  ACTIVE: 'active',
  NOTIFIED: 'notified',
  TIMEOUT: 'timeOut',
  REMOVED: 'removed',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
  ACCEPTED: 'accepted',
  AUTONOTIFY_ON: 'autonotify',
};

const getStatusPill = (status) => {
  switch (status) {
    case WalkInSlotStatus.ACCEPTED:
      return <span className="pill pill--green">{t('appointment:walk_in_list.content.status.booked')}</span>;
    case WalkInSlotStatus.AUTONOTIFY_ON:
      return <span className="pill pill--gray">{t('appointment:walk_in_list.content.status.auto_notify_on')}</span>;
    case WalkInSlotStatus.NOTIFIED:
      return <span className="pill pill--orange">{t('appointment:walk_in_list.content.status.notified')}</span>;
    default:
      return null;
  }
};

const getCTA = (status, onAccept, onNotify, isNotifyingClient, isFirstActive = false) => {
  switch (status) {
    case WalkInSlotStatus.ACTIVE:
      if (isFirstActive) {
        return (
          <button className="cta-btn cta-btn--blue" onClick={onAccept}>
            <Icon glyph={GLYPHS.checked} size="small" />
            {t('appointment:walk_in_list.content.accept')}
          </button>
        );
      }
      return (
        <button className="cta-btn cta-btn--blue" onClick={onNotify} disabled={isNotifyingClient}>
          {isNotifyingClient ? (
            <Icon glyph={GLYPHS.spinner} size="small" />
          ) : (
            <Icon glyph={GLYPHS.clock} size="small" />
          )}
          {isNotifyingClient
            ? t('appointment:walk_in_list.content.notifying')
            : t('appointment:walk_in_list.content.notify')}
        </button>
      );
    case WalkInSlotStatus.NOTIFIED:
      return (
        <button className="cta-btn cta-btn--blue" onClick={onAccept}>
          <Icon glyph={GLYPHS.checked} size="small" />
          {t('appointment:walk_in_list.content.accept')}
        </button>
      );
    case WalkInSlotStatus.TIMEOUT:
    case WalkInSlotStatus.REMOVED:
    case WalkInSlotStatus.CANCELED:
    case WalkInSlotStatus.EXPIRED:
      return (
        <button className="cta-btn cta-btn--blue" onClick={onNotify} disabled={isNotifyingClient}>
          {isNotifyingClient ? (
            <>
              <Icon glyph={GLYPHS.spinner} size="small" />
              {t('appointment:walk_in_list.content.notifying')}
            </>
          ) : (
            <>
              <Icon glyph={GLYPHS.notification} size="small" />
              {t('appointment:walk_in_list.content.notify')}
            </>
          )}
        </button>
      );
    default:
      return null;
  }
};

const WalkInListContent = ({
  clients,
  isLoading,
  activeView,
  onHandleAccept,
  onHandleNotify,
  onRemoveClient,
  isNotifyingClientById,
}) => {
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [notesModalClient, setNotesModalClient] = useState(null);

  useEffect(() => {
    setMenuOpenId(null);
  }, [clients]);

  if (isLoading) {
    return (
      <div className="walk-in-list-content">
        <div className="walk-in-list-loading">
          <div className="loading-spinner" />
          <p>{t('appointment:walk_in_list.content.loading')}</p>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="walk-in-list-content">
        <div className="walk-in-list-empty">
          <p>
            {activeView === 'current'
              ? t('appointment:walk_in_list.content.no_clients_today')
              : t('appointment:walk_in_list.content.no_clients_date')}
          </p>
        </div>
      </div>
    );
  }

  const handleAccept = (client) => {
    onHandleAccept(client);
  };

  const handleNotify = (client) => {
    onHandleNotify(client);
  };

  const handleMenuOpen = (clientId) => setMenuOpenId(clientId);

  const handleMenuClose = () => setMenuOpenId(null);

  const filteredClients = clients.filter((client) => client.status !== 'removed');
  const firstActiveClientIndex = filteredClients.findIndex((client) => client.status === WalkInSlotStatus.ACTIVE);

  return (
    <div className="walk-in-list-content">
      <div className="walk-in-list-clients">
        {filteredClients.map((client, index) => {
          const avatarUrl =
            client.customer.photos && client.customer.photos.length > 0 ? client.customer.photos[0].thumbnail : null;
          const serviceName = client.services && client.services.length > 0 ? client.services[0].name : '';
          const isWalkIn = client.type === 'walk-in';
          const pillLabel = isWalkIn
            ? t('appointment:walk_in_list.content.walk_in_label')
            : t('appointment:walk_in_list.content.waitlist_label');
          const pillClass = isWalkIn ? 'pill--blue' : 'pill--gray';
          const statusPill = getStatusPill(client.status);
          const isFirstActive = index === firstActiveClientIndex;
          const cta = getCTA(
            client.status,
            () => handleAccept(client),
            () => handleNotify(client),
            isNotifyingClientById(client.id),
            isFirstActive
          );
          return (
            <div key={client.id} className="walk-in-list-client walk-in-list-grid-row">
              <div className="walkin-col user-info">
                <span className="client-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="c-avatar-image" />
                  ) : (
                    <Icon glyph={GLYPHS.customers} size="medium" />
                  )}
                </span>
                <div className="client-user-details">
                  <div className="client-name">
                    {client.customer.firstName} {client.customer.lastName}
                  </div>
                  <div className="client-contact">
                    {client.customer.phone ? client.customer.phone : client.customer.email}
                  </div>
                </div>
              </div>
              <div className="walkin-col service-info">
                <div className="service-name">{serviceName}</div>
                <div className="service-info-container">
                  <span className={`pill ${pillClass}`}>{pillLabel}</span>
                  <div className="barber-name">
                    {client.barberName || t('appointment:walk_in_list.content.any_barber')}
                  </div>
                </div>
              </div>
              <div className="walkin-col cta-info">
                <div
                  style={
                    client.status === WalkInSlotStatus.NOTIFIED
                      ? { display: 'flex', alignItems: 'center', gap: '8px' }
                      : {}
                  }
                >
                  {client.status === WalkInSlotStatus.NOTIFIED ? (
                    <>
                      {statusPill}
                      {cta}
                    </>
                  ) : (
                    statusPill || cta
                  )}
                </div>
                <ContextMenu
                  className="c-border-none"
                  isOpen={menuOpenId === client.id}
                  handleClick={() => {
                    if (menuOpenId === client.id) {
                      handleMenuClose();
                    } else {
                      handleMenuOpen(client.id);
                    }
                  }}
                  closeMenu={handleMenuClose}
                  label={<Icon glyph={GLYPHS.more} size="small" />}
                  showIcon={false}
                >
                  <button
                    className="walkin-col__btn"
                    onClick={() => {
                      setNotesModalClient(client);
                      handleMenuClose();
                    }}
                  >
                    <Icon glyph={GLYPHS.textParagraphSpacing} size="small" />
                    <span>{t('appointment:walk_in_list.content.view_notes')}</span>
                  </button>
                  <button
                    className="walkin-col__btn"
                    onClick={() => {
                      onRemoveClient(client.id);
                      handleMenuClose();
                    }}
                  >
                    <Icon glyph={GLYPHS.trash} size="small" />
                    <span>{t('appointment:walk_in_list.content.remove')}</span>
                  </button>
                </ContextMenu>
              </div>
            </div>
          );
        })}
      </div>
      <NotesModal
        isOpen={!!notesModalClient}
        onRequestClose={() => setNotesModalClient(null)}
        client={notesModalClient}
      />
    </div>
  );
};

WalkInListContent.propTypes = {
  clients: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  activeView: PropTypes.string.isRequired,
  onHandleAccept: PropTypes.func.isRequired,
  onHandleNotify: PropTypes.func.isRequired,
  onRemoveClient: PropTypes.func.isRequired,
  isNotifyingClientById: PropTypes.func.isRequired,
};

export { WalkInListContent };
export default WalkInListContent;
